import { Injectable } from '@angular/core'
import { Warehouse, WarehousesService } from '../warehouses'
import { Product, ProductsService } from '../products'
import { Location, LocationsService } from '../locations'
import { User, UsersService } from '../users'
import {
  Observable,
  combineLatest,
  from,
  last,
  concatMap,
  of,
  scan,
  switchMap,
  map,
} from 'rxjs'
import { ReplenishmentsService } from './replenishments.service'
import { Page } from '../../models/util.model'
import { uniq, difference, uniqBy } from 'lodash'
import { NgNotificationServiceInterface } from '../../models/notification.model'
import {
  ModalResponse,
  NgModalServiceInterface,
} from '../../models/modal.model'
import { parseRepPageKeys } from './libs/replenishment-search.lib'
import {
  parseReplenishmentModal,
  parseReplenishmentNotification,
  ReplenishmentModalCode,
  ReplenishmentNotificationCode,
} from './libs/replenishment-notification.lib'
import { ReplenishmentSearchParams } from './models/replenishment-search.model'
import {
  ReplenishmentsListingData,
  ReplenishmentsListingPage,
} from './models/replenishment-listing.model'
import { Replenishment } from './models/replenishment.entity'
import { ReplenishmentField } from './models/replenishment.types'

@Injectable({
  providedIn: 'root',
})
export class ReplenishmentsRepository {
  constructor(
    private replenishmentsService: ReplenishmentsService,
    private warehousesService: WarehousesService,
    private locationsService: LocationsService,
    private usersService: UsersService,
    private productsService: ProductsService,
    private ngModalService?: NgModalServiceInterface,
    private ngNotificationService?: NgNotificationServiceInterface,
  ) {}

  /**
   * Search replenishments and external data
   * @param searchParams - the search params
   * @param fields - the fields
   * @returns the observable for search replenishments and external data
   */
  searchReplenishments$(
    searchParams: ReplenishmentSearchParams,
    fields: ReplenishmentField[],
    listingData?: ReplenishmentsListingData,
    returnAll?: boolean,
  ): Observable<ReplenishmentsListingPage> {
    const needsPagination =
      searchParams.limit !== undefined &&
      searchParams.limit > this.replenishmentsService.limit

    return needsPagination
      ? this._paginateReplenishments$(searchParams, fields, listingData)
      : this._searchReplenishments$(
          searchParams,
          fields,
          listingData,
          returnAll,
        )
  }

  /**
   * Search goods returns with relative external data
   * @param searchParams - the search params
   * @param fields - the goods-returns fields
   * @param listingData - the already loaded listing data
   * @returns the observable<ReplenishmentsListingPage> for search goods-returns
   */
  private _searchReplenishments$(
    searchParams: ReplenishmentSearchParams,
    fields: ReplenishmentField[],
    listingData?: ReplenishmentsListingData,
    returnAll?: boolean,
  ): Observable<ReplenishmentsListingPage> {
    return this.replenishmentsService
      .search$(searchParams, returnAll)
      .pipe(
        switchMap((page) =>
          this._loadExtData$(page, fields, listingData).pipe(
            map((extData) => ({ ...page, extData })),
          ),
        ),
      )
  }

  /**
   * Search more than 200 goods-returns with relative external data
   * @param searchParams - the search params
   * @param fields - the goods-returns fields
   * @param listingData - the already loaded listing data
   * @returns the observable<ReplenishmentsListingPage> for search goods-returns
   */
  private _paginateReplenishments$(
    searchParams: ReplenishmentSearchParams,
    fields: ReplenishmentField[],
    listingData?: ReplenishmentsListingData,
  ): Observable<ReplenishmentsListingPage> {
    const totalCount = searchParams.limit || this.replenishmentsService.limit
    const pagesCount = Math.ceil(totalCount / this.replenishmentsService.limit)

    return from(
      Array.from({ length: pagesCount }, (_, i) => ({
        ...searchParams,
        limit: this.replenishmentsService.limit,
        offset: i * this.replenishmentsService.limit,
      })),
    ).pipe(
      concatMap((pageParams) =>
        this._searchReplenishments$(pageParams, fields, listingData),
      ),
      scan<ReplenishmentsListingPage, ReplenishmentsListingPage>(
        (acc, current) => ({
          totalCount: uniqBy(
            [...(acc.data || []), ...(current.data || [])],
            '_id',
          ).length,
          data: uniqBy([...(acc.data || []), ...(current.data || [])], '_id'),
          extData: { ...acc.extData, ...current.extData },
        }),
        { totalCount: 0, data: [], extData: {} } as ReplenishmentsListingPage,
      ),
      last(),
    )
  }

  /**
   * Notify a message about a replenishment event
   * @param notification - the replenishment notification
   */
  notify(options: {
    code?: ReplenishmentNotificationCode
    message?: string
  }): void {
    this.ngNotificationService?.show(
      options.message
        ? { title: 'Error', message: options.message, color: 'error' }
        : parseReplenishmentNotification(options.code),
    )
  }

  async alert(
    code: ReplenishmentModalCode,
    replenishment?: Replenishment,
  ): Promise<ModalResponse> {
    const settings = parseReplenishmentModal(code, replenishment)
    if (!this.ngModalService || !settings) {
      return 'closed'
    }
    return await this.ngModalService.showDialog(settings)
  }

  /**
   * Load page external data
   * @param page - the replenishments page
   * @param fields - the fields
   * @returns the observable for load external data
   */
  private _loadExtData$(
    page: Page<Replenishment>,
    fields: ReplenishmentField[],
    listingData?: ReplenishmentsListingData,
  ): Observable<ReplenishmentsListingData> {
    const extDataKeys = parseRepPageKeys(page)
    const obs$: Partial<{
      warehouses: Observable<Warehouse[]>
      users: Observable<User[]>
      products: Observable<Product[]>
      locations: Observable<Location[]>
    }> = {}

    if (fields.includes('warehouseId') && extDataKeys.warehouseIds) {
      obs$['warehouses'] = this.warehousesService.store$(
        extDataKeys.warehouseIds,
        listingData?.warehouses || [],
      )
    }

    if (fields.includes('userId') && extDataKeys.userIds) {
      obs$['users'] = this.usersService.store$(
        extDataKeys.userIds,
        listingData?.users || [],
      )
    }

    if (fields.includes('product._id') && extDataKeys.productIds) {
      obs$['products'] = this.productsService.store$(
        extDataKeys.productIds,
        listingData?.products || [],
      )
    }

    if (
      (fields.includes('target._id') || fields.includes('source._id')) &&
      extDataKeys.locationIds
    ) {
      obs$['locations'] = this.locationsService
        .store$(extDataKeys.locationIds, listingData?.locations || [])
        .pipe(
          switchMap((locations) => {
            const dockIds = uniq(
              locations.filter((loc) => loc.dockId).map((loc) => loc.dockId!),
            )

            if (dockIds.length === 0) {
              return of(locations)
            }

            const existingLocationIds = (listingData?.locations || []).map(
              (l) => l._id,
            )
            const missingDockIds = difference(dockIds, existingLocationIds)

            if (missingDockIds.length === 0) {
              return of(locations)
            }

            return this.locationsService.store$(missingDockIds, locations)
          }),
        )
    }

    if (!Object.keys(obs$).length) {
      return of({} as ReplenishmentsListingData)
    }

    return combineLatest(obs$).pipe(
      map((results) => {
        const extData: ReplenishmentsListingData = {}
        if (results.warehouses) extData.warehouses = results.warehouses
        if (results.users) extData.users = results.users
        if (results.products) extData.products = results.products
        if (results.locations) extData.locations = results.locations
        return extData
      }),
    )
  }
}
